import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getBrandConfig, DefaultBrand } from '@/brand'
import setBrandConfig from '@/functions/setBrandConfig'

const LANGUAGE_STORAGE_KEY = 'wr-lp-lang'
const kebab = (key) => key.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`)

export const useBrandStore = defineStore('brand', () => {
  const brand = ref(DefaultBrand)
  const language = ref(getBrandConfig(DefaultBrand).language)

  const brandConfig = computed(() => getBrandConfig(brand.value))
  const languages = computed(() => brandConfig.value.languages || [brandConfig.value.language])
  const translations = computed(
    () => brandConfig.value.translations[language.value] || brandConfig.value.translations[brandConfig.value.language]
  )
  const isRTL = computed(() => brandConfig.value.isRTL)
  const locale = computed(() => (language.value === 'fr' ? 'fr-FR' : 'en-GB'))

  // Brand theme as CSS variables (colorBg -> --color-bg), applied on the app root in App.vue.
  const themeStyle = computed(() =>
    Object.fromEntries(Object.entries(brandConfig.value.theme || {}).map(([key, value]) => [`--${kebab(key)}`, value]))
  )

  // t('otpResendIn', { s: 30 }) -> 'Renvoyer dans 30 s'. Unknown keys render as the key so a missing
  // string is visible rather than blank.
  function t(key, vars = {}) {
    const text = translations.value[key] ?? key
    return String(text).replace(/\{(\w+)\}/g, (match, name) => (vars[name] ?? match))
  }

  function setLanguage(next) {
    if (!languages.value.includes(next)) return
    language.value = next
    document.documentElement.lang = next
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, next)
    } catch {
      // storage unavailable (private mode): the choice lasts for this page view only
    }
  }

  // ?lang= wins, then the remembered choice, then the brand default (French for Cameroon).
  function resolveLanguage() {
    const requested = new URLSearchParams(window.location.search).get('lang')
    let stored = null
    try {
      stored = localStorage.getItem(LANGUAGE_STORAGE_KEY)
    } catch {
      stored = null
    }
    const candidate = [requested, stored, brandConfig.value.language].find((value) => languages.value.includes(value))
    setLanguage(candidate || brandConfig.value.language)
  }

  function resolveBrandFromHost() {
    brand.value = setBrandConfig()
    resolveLanguage()
  }

  return { brand, language, languages, locale, brandConfig, translations, isRTL, themeStyle, t, setLanguage, resolveBrandFromHost }
})
